// @flow

import React, { Component } from 'react';
import type { Node } from 'react';
import Siema from 'siema';
import {
  Navigation,
  SliderContainer,
  Container,
  ForwardButton,
  BackwardButton,
} from './atoms';
import { breakpointsPx } from '../../helpers/breakpoints';

type State = {
  currentSlide: number,
  initialized: boolean,
  totalSlides: number,
  perPage: number,
};

type Props = {
  loop?: boolean,
  perPage?: number | Object,
  totalSlides: number,
  children: Node,
  arrowNavigation?: boolean,
  bulletNavigation?: boolean,
};

class Slider extends Component<Props, State> {
  sliderEl: ?HTMLDivElement;

  slider: {
    prev: Function,
    next: Function,
    currentSlide: number,
    goTo: Function,
    perPage: number,
    destroy: Function,
  };

  static defaultProps = {
    arrowNavigation: true,
    bulletNavigation: true,
    loop: false,
    perPage: {
      [breakpointsPx.lg]: 3,
      [breakpointsPx.md]: 2,
      [breakpointsPx.sm]: 1,
    },
  };

  constructor(props: Props) {
    super(props);

    this.state = {
      currentSlide: 0,
      initialized: false,
      perPage: 4,
      totalSlides: props.totalSlides,
    };
  }

  componentDidMount() {
    const { loop, perPage } = this.props;
    this.slider = new Siema({
      loop,
      perPage,
      selector: this.sliderEl,
      onInit: this.onSiemaInit,
      onChange: this.onSiemaChange,
    });

    this.checkPerPage();
    window.addEventListener('resize', this.checkPerPage);
  }

  shouldComponentUpdate(nextProps: Props, nextState: State) {
    const { currentSlide, initialized } = this.state;
    return currentSlide !== nextState.currentSlide
      || initialized !== nextState.initialized;
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.checkPerPage);
    this.slider.destroy();
  }

  onSiemaInit = () => {
    this.setState({
      initialized: true,
    });
  }

  onSiemaChange = () => {
    this.setState({
      currentSlide: this.slider.currentSlide,
    });
  }

  checkPerPage = () => {
    this.setState({
      perPage: this.slider.perPage,
    });
  }


  isFirstSlide() {
    const { currentSlide } = this.state;
    return currentSlide === 0;
  }

  isLastSlide() {
    const { totalSlides, perPage, currentSlide } = this.state;
    return (totalSlides - perPage) === currentSlide;
  }

  hasForwardButton() {
    return !this.isLastSlide() && this.navigationNeeded();
  }

  navigationNeeded() {
    const { totalSlides, perPage } = this.state;
    return totalSlides > perPage;
  }

  render() {
    const { children, arrowNavigation, bulletNavigation } = this.props;
    const { totalSlides, perPage, currentSlide } = this.state;
    return (
      <Container bulletNavigation={bulletNavigation}>
        {arrowNavigation && !this.isFirstSlide() && (
          <BackwardButton onClick={() => this.slider.prev()} />
        )}
        <SliderContainer ref={el => { this.sliderEl = el }}>
          {children}
        </SliderContainer>
        {arrowNavigation && this.hasForwardButton() && (
          <ForwardButton onClick={() => this.slider.next()} />
        )}
        {bulletNavigation && this.navigationNeeded() && (
          <Navigation
            slideCount={((totalSlides - perPage) + 1)}
            selectedSlide={currentSlide}
            onChangeSlide={index => this.slider.goTo(index)}
          />
        )}
      </Container>
    );
  }
}

export default Slider;
